import React, { useEffect, useState } from 'react';
import { ArrowLeft, Heart, MessageCircle, Clock, ImagePlus, X } from 'lucide-react';
import postService, {
  DartPost,
  getImageUrl,
  formatRelativeTime,
} from '../../services/postService';
import { t } from '../../i18n';
import { appAlert } from '../alerts/appAlert';

import ArrowScrollRow from '../../components/common/ArrowScrollRow';
const ACCENT = '#3fced1';
const MAX_IMAGES = 10;

/** Bild im Editor: vorhandener Dateiname ODER neue base64-Data-URL */
interface EditImage {
  /** Dateiname des vorhandenen Bildes (behalten) */
  existingName?: string;
  /** data:-URL eines neu gewählten Bildes */
  dataUrl?: string;
}

interface PostEditorProps {
  post: DartPost;
  isOpen: boolean;
  onClose: () => void;
  /** Nach erfolgreichem Speichern (aktualisierter Post) */
  onSaved: (post: DartPost) => void;
}

/**
 * Vollbild-Editor "Beitrag bearbeiten" (Marcel 2026-08-31) — Web-Pendant
 * zum App-PostEditScreen und Marcels Fullscreen-Regel: fixed inset-0 ÜBER
 * der Navbar mit App-Style-Kopfzeile (Back-Pfeil + Titel).
 * Stats + Bilder verwalten (min. 1) + Beschreibung.
 */
const PostEditor: React.FC<PostEditorProps> = ({ post, isOpen, onClose, onSaved }) => {
  const [images, setImages] = useState<EditImage[]>([]);
  const [caption, setCaption] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setImages((post.images ?? []).map((name) => ({ existingName: name })));
    setCaption(post.caption ?? '');
    setSaving(false);
  }, [isOpen, post]);

  if (!isOpen) return null;

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const remaining = MAX_IMAGES - images.length;
    Array.from(files)
      .slice(0, remaining)
      .forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = typeof reader.result === 'string' ? reader.result : null;
          if (dataUrl) setImages((prev) => [...prev, { dataUrl }]);
        };
        reader.readAsDataURL(file);
      });
  };

  const save = async () => {
    if (saving) return;
    // Rückmeldungen als zentraler Alert (Marcel 2026-10-06)
    if (images.length === 0) {
      appAlert.warning(t('web.component.post-editor.mindestens-ein-bild-ist-pflicht'));
      return;
    }
    setSaving(true);
    const payload = images
      .map((img) => img.existingName ?? img.dataUrl)
      .filter((v): v is string => Boolean(v));
    const updated = await postService.updatePost(post.id, caption, payload);
    if (updated) {
      onSaved(updated);
      onClose();
    } else {
      setSaving(false);
      appAlert.error(t('web.component.post-editor.speichern-fehlgeschlagen-bitte-versuche'));
    }
  };

  const stat = (Icon: typeof Heart, value: string, label: string) => (
    <div className="flex flex-1 flex-col items-center gap-1 rounded-xl border border-zinc-800 py-3">
      <Icon size={17} style={{ color: ACCENT }} />
      <span className="text-base font-extrabold text-white">{value}</span>
      <span className="text-[11px] text-gray-500">{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-zinc-950">
      {/* App-Style-Kopfzeile: Back-Pfeil + Titel + Speichern */}
      <div className="flex items-center gap-3 border-b border-zinc-800 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('web.component.chats-manager.zurueck')}
          className="rounded-full p-1.5 text-zinc-300 hover:bg-zinc-900"
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="flex-1 text-base font-bold text-white">{t('web.component.edit-post-modal.beitrag-bearbeiten')}</h2>
        <button
          type="button"
          onClick={save}
          disabled={saving || images.length === 0}
          className="rounded-xl px-4 py-1.5 text-sm font-bold text-black disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}
        >
          {saving ? t('web.component.post-editor.speichert') : t('web.component.lobby-form-modal.speichern')}
        </button>
      </div>

      <div className="mx-auto w-full max-w-[600px] flex-1 overflow-y-auto px-4 py-5">
        {/* Stats */}
        <p className="mb-2 text-[13px] font-semibold text-gray-400">{t('web.component.post-editor.statistiken')}</p>
        <div className="mb-6 flex gap-2.5">
          {stat(Heart, String(post.likeCount ?? 0), 'Likes')}
          {stat(MessageCircle, String(post.commentCount ?? 0), 'Kommentare')}
          {stat(Clock, formatRelativeTime(post.createdAt), t('web.component.post-editor.veroeffentlicht'))}
        </div>

        {/* Bilder */}
        <p className="mb-2 text-[13px] font-semibold text-gray-400">
          {t('web.component.post-editor.bilder-min-1-max', { MAX_IMAGES })}
        </p>
        <ArrowScrollRow testId="post-images" className="mb-6" scrollClassName="flex gap-2 pb-1">
          {images.map((img, i) => (
            <div key={img.existingName ?? `neu-${i}`} className="relative shrink-0">
              <img
                src={img.existingName ? getImageUrl(img.existingName) ?? '' : img.dataUrl}
                alt=""
                className="h-[150px] w-[120px] rounded-lg object-cover"
              />
              <button
                type="button"
                aria-label={t('web.component.post-editor.bild-entfernen')}
                onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                className="absolute right-1 top-1 rounded-full bg-black/65 p-1 text-white"
              >
                <X size={13} />
              </button>
            </div>
          ))}
          {images.length < MAX_IMAGES && (
            <label className="flex h-[150px] w-[120px] shrink-0 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-zinc-800 text-xs text-zinc-300 hover:border-zinc-700">
              <ImagePlus size={26} style={{ color: ACCENT }} />
              {t('web.component.listing-admins-card.hinzufuegen')}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </ArrowScrollRow>

        {/* Beschreibung */}
        <p className="mb-2 text-[13px] font-semibold text-gray-400">{t('web.component.report-modal.beschreibung')}</p>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          maxLength={2200}
          rows={5}
          className="w-full rounded-xl border border-zinc-800 bg-zinc-900 p-3 text-sm text-zinc-200 focus:border-[#3fced1]/60 focus:outline-none"
          placeholder={t('web.component.post-editor.worum-geht-es-in-deinem')}
        />
        <p className="mt-1 text-right text-[11px] text-gray-600">{caption.length}/2200</p>

      </div>
    </div>
  );
};

export default PostEditor;
